import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Autocomplete,
    TextField,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import BoltIcon from '@mui/icons-material/Bolt';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

let _savedPos = null;

const PANEL_ANIMATION = `
@keyframes macroButtonFadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
}
`;

const ButtonMacro = ({
    macroList,
    macroSelected,
    macroSelectedActions,
    loadingRequestMacroList,
    loadingMacroAccordion,
    isApplyingMacro,
    onSelectMacro,
    onApply,
    onSave,
    onDismiss,
    userId,
}) => {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [dragging, setDragging] = useState(false);
    const anchorRef = useRef(null);
    const containerRef = useRef(null);
    const posRef = useRef(null);

    // Persistência da posição por usuário (localStorage). Sem userId cai numa chave genérica.
    const storageKey = `mexx.buttonmacro.pos${userId ? '.' + userId : ''}`;

    // Mantém a posição dentro da viewport — evita o botão "sumir" fora da tela quando a posição
    // salva foi feita numa resolução maior, ou após redimensionar a janela.
    const clampToViewport = (top, left, w, h) => {
        if (typeof window === 'undefined') return { top, left };
        const width = w || 220;
        const height = h || 46;
        const maxLeft = Math.max(8, window.innerWidth - width - 8);
        const maxTop = Math.max(8, window.innerHeight - height - 8);
        return {
            top: Math.min(Math.max(8, top), maxTop),
            left: Math.min(Math.max(8, left), maxLeft),
        };
    };

    const [buttonPos, setButtonPos] = useState(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) {
                const p = JSON.parse(raw);
                if (p && typeof p.top === 'number' && typeof p.left === 'number') return p;
            }
        } catch (e) { /* localStorage indisponível (modo privado/quota) — segue com default */ }
        return _savedPos;
    });

    const hasActions = macroSelectedActions && macroSelectedActions.length > 0;
    const isLoadingActions = loadingMacroAccordion && loadingMacroAccordion !== false;

    const handleToggle = () => setOpen(prev => !prev);

    // Posição inicial (1º uso, sem nada salvo): replica o layout anterior — ao lado do título
    // (top do anchor) e ~65% da largura. A partir daí o usuário arrasta livremente pelo handle.
    useEffect(() => {
        if (buttonPos || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const initial = clampToViewport(
            rect.top - 18,
            typeof window !== 'undefined' ? Math.round(window.innerWidth * 0.65) : rect.left
        );
        _savedPos = initial;
        setButtonPos(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pos = buttonPos ?? { top: 152, left: typeof window !== 'undefined' ? Math.round(window.innerWidth * 0.65) : 0 };
    posRef.current = pos;

    // Arrasto pelo HANDLE dedicado (ícone). Pointer events cobrem mouse e toque. Como o handle é
    // separado da pílula "Automatizar", não há ambiguidade clique-vs-arrasto: o clique na pílula
    // continua só abrindo/fechando o painel. Ao soltar, persiste a posição no localStorage.
    const beginDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : null;
        const w = rect ? rect.width : 220;
        const h = rect ? rect.height : 46;
        const baseTop = posRef.current.top;
        const baseLeft = posRef.current.left;
        setDragging(true);

        const onMove = (ev) => {
            const next = clampToViewport(baseTop + (ev.clientY - startY), baseLeft + (ev.clientX - startX), w, h);
            posRef.current = next;
            setButtonPos(next);
        };
        const onUp = () => {
            setDragging(false);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            _savedPos = posRef.current;
            try { window.localStorage.setItem(storageKey, JSON.stringify(posRef.current)); } catch (er) { /* ignora */ }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const floatingUI = (
        <>
            <style>{PANEL_ANIMATION}</style>
            <div ref={containerRef} style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 1200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}>
                {/* Toggle button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {/* Handle de arrasto (mãozinha): clica, segura e reposiciona o botão. touchAction:none
                        impede o toque de rolar a página durante o arrasto. */}
                    <Tooltip title="Arraste para reposicionar" placement="right">
                        <span
                            onPointerDown={beginDrag}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: dragging ? 'grabbing' : 'grab',
                                color: '#fff',
                                background: 'var(--mexx-green-primary, #2979ff)',
                                borderRadius: 999,
                                padding: '7px 3px',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                touchAction: 'none',
                                userSelect: 'none',
                            }}
                        >
                            <DragIndicatorIcon htmlColor="#fff" style={{ fontSize: 16, color: '#fff' }} />
                        </span>
                    </Tooltip>
                    <Tooltip title={open ? 'Fechar macros' : 'Automatizar com macro'} placement="right">
                        <button
                            onClick={handleToggle}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                            style={{
                                background: open
                                    ? 'var(--mexx-green-dark, #3f51b4)'
                                    : 'var(--mexx-green-primary, #2979ff)',
                                color: '#fff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                transition: 'all .2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <BoltIcon style={{ fontSize: 16, color: '#fff' }} />
                            Automatizar
                        </button>
                    </Tooltip>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
                        title="Fechar"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'rgba(80,80,80,0.5)',
                            cursor: 'pointer',
                            padding: '2px 3px',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 1,
                        }}
                    >
                        <CloseIcon style={{ fontSize: 13 }} />
                    </button>
                </div>

                {/* Panel */}
                {open && (
                    <div style={{
                        width: 320,
                        marginTop: 8,
                        background: 'var(--mexx-bg-elevated, #fff)',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                        padding: '16px',
                        border: '1px solid var(--mexx-gray-200, #EEEEEE)',
                        animation: 'macroButtonFadeUp .2s ease',
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 14,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <BoltIcon style={{ fontSize: 18, color: 'var(--mexx-green-primary, #2979ff)' }} />
                                <Typography style={{ fontWeight: 600, fontSize: 14, color: 'var(--mexx-gray-800, #424242)' }}>
                                    Automatizar com macro
                                </Typography>
                            </div>
                            <IconButton size="small" onClick={handleToggle} style={{ color: 'var(--mexx-gray-500, #9E9E9E)' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* Macro select */}
                        <Autocomplete
                            size="small"
                            options={macroList || []}
                            getOptionLabel={opt => opt.name || ''}
                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            value={macroSelected || null}
                            loading={loadingRequestMacroList}
                            onChange={(_, newVal) => onSelectMacro(newVal)}
                            disablePortal
                            componentsProps={{
                                popper: {
                                    style: { zIndex: 10 },
                                },
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Selecionar macro"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingRequestMacroList
                                                    ? <CircularProgress size={14} color="inherit" />
                                                    : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--mexx-green-primary, #2979ff)',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: 'var(--mexx-green-primary, #2979ff)',
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Actions preview */}
                        {isLoadingActions && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                                <CircularProgress size={20} style={{ color: 'var(--mexx-green-primary, #2979ff)' }} />
                            </div>
                        )}

                        {!isLoadingActions && hasActions && (
                            <div style={{
                                marginTop: 12,
                                background: 'var(--mexx-green-subtle, #e9edf8)',
                                borderRadius: 8,
                                padding: '8px 10px',
                            }}>
                                {macroSelectedActions.map(action => (
                                    <div key={action.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 4,
                                    }}>
                                        <CheckCircleOutlineIcon style={{
                                            fontSize: 14,
                                            color: 'var(--mexx-green-primary, #2979ff)',
                                            flexShrink: 0,
                                        }} />
                                        <Typography style={{ fontSize: 12, color: 'var(--mexx-gray-700, #616161)' }}>
                                            {action.automationactionname}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Buttons */}
                        {hasActions && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                {isApplyingMacro != null && onSave ? (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        startIcon={<SaveIcon />}
                                        onClick={onSave}
                                        sx={{
                                            background: 'var(--mexx-green-primary, #2979ff)',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            '&:hover': {
                                                background: 'var(--mexx-green-dark, #3f51b4)',
                                            },
                                        }}
                                    >
                                        Salvar chamado
                                    </Button>
                                ) : (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        startIcon={isApplyingMacro != null
                                            ? <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#fff' }} />
                                            : <BoltIcon style={{ fontSize: 16, color: '#fff' }} />
                                        }
                                        disabled={isApplyingMacro != null}
                                        onClick={onApply}
                                        sx={{
                                            background: 'var(--mexx-green-primary, #2979ff)',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            '&:hover': {
                                                background: 'var(--mexx-green-dark, #3f51b4)',
                                            },
                                            '&.Mui-disabled': {
                                                background: 'var(--mexx-green-primary, #2979ff)',
                                                opacity: 0.7,
                                                color: '#fff',
                                            },
                                        }}
                                    >
                                        {isApplyingMacro != null ? 'Macro aplicada' : 'Aplicar macro'}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    return (
        <>
            {/* Invisible anchor — marks the target position next to the title */}
            <span ref={anchorRef} style={{ display: 'inline-block' }} />
            {createPortal(floatingUI, document.body)}
        </>
    );
};

export default ButtonMacro;
